import { useEffect, useRef } from 'react';

interface LightningOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function LightningOverlay({ colors, opacity = 0.3 }: LightningOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let lightningBolts: LightningBolt[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent RGB updated each frame
    let accentRgb: [number, number, number] = [10,116,255];

    class LightningBolt {
      startX: number;
      startY: number;
      segments: Array<{ x1: number; y1: number; x2: number; y2: number }>;
      opacity: number;
      decay: number;

      constructor() {
        this.startX = Math.random() * canvas.width;
        this.startY = 0;
        this.segments = [];
        this.opacity = 1;
        this.decay = 0.05;
        this.generateBolt();
      }

      generateBolt() {
        let x = this.startX;
        let y = this.startY;
        const targetY = canvas.height;
        
        while (y < targetY) {
          const nextX = x + (Math.random() - 0.5) * 100;
          const nextY = y + Math.random() * 50 + 30;
          
          this.segments.push({ x1: x, y1: y, x2: nextX, y2: nextY });
          
          // Random branches
          if (Math.random() < 0.3) {
            this.createBranch(x, y, nextX - x, nextY - y);
          }
          
          x = nextX;
          y = nextY;
        }
      }

      createBranch(startX: number, startY: number, dx: number, dy: number) {
        const branchLength = Math.random() * 3 + 2;
        let x = startX;
        let y = startY;
        
        for (let i = 0; i < branchLength; i++) {
          const nextX = x + dx * 0.5 + (Math.random() - 0.5) * 50;
          const nextY = y + dy * 0.5 + Math.random() * 30;
          this.segments.push({ x1: x, y1: y, x2: nextX, y2: nextY });
          x = nextX;
          y = nextY;
        }
      }

      update() {
        this.opacity -= this.decay;
      }

      draw() {
        this.segments.forEach(segment => {
          // Main bolt
          ctx.beginPath();
          ctx.moveTo(segment.x1, segment.y1);
          ctx.lineTo(segment.x2, segment.y2);
          const [r,g,b] = accentRgb;
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${this.opacity})`;
          ctx.lineWidth = 3;
          ctx.stroke();
          
          // Glow
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${this.opacity * 0.5})`;
          ctx.lineWidth = 6;
          ctx.stroke();
          
          // Outer glow
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${this.opacity * 0.3})`;
          ctx.lineWidth = 10;
          ctx.stroke();
        });
      }
    }

    const createLightning = () => {
      if (Math.random() < 0.02) {
        lightningBolts.push(new LightningBolt());
      }
    };

    const animate = () => {
      const isLightMode = document.documentElement.getAttribute('data-theme') === 'light';
      ctx.fillStyle = isLightMode ? 'rgba(248, 250, 252, 0.1)' : 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      accentRgb = parseCssColorToRgb(accent);

      createLightning();

      lightningBolts = lightningBolts.filter(bolt => bolt.opacity > 0);
      lightningBolts.forEach(bolt => {
        bolt.update();
        bolt.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
